// Waiver bid state for the current member: own bids (all statuses, readable
// under fa_bids_read_own regardless of status) plus the RPC wrappers.
// Shared by the Lineup tab's pending-bids chip (Phase 3) and the Teams tab's
// bid sheet (Phase 5).
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/supabase';

// RPC convention mirrors DraftContext.js's makePick: return the transport
// error message on failure, otherwise the RPC's own jsonb payload verbatim
// (including place_bid's budget field on an over-budget error) so the UI can
// surface exactly what the backend says without reshaping it.
async function callRpc(name, params, { onSuccess } = {}) {
  const { data, error } = await supabase.rpc(name, params);
  if (error) return { error: error.message };
  if (!data?.error && onSuccess) await onSuccess();
  return data;
}

export function useWaiverBids({ leagueId, memberId }) {
  const [bids, setBids] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!leagueId || !memberId) { setBids([]); setLoading(false); return; }
    const { data } = await supabase
      .from('fa_bids')
      .select('*')
      .eq('league_id', leagueId)
      .eq('member_id', memberId)
      .order('created_at', { ascending: false });
    setBids(data || []);
    setLoading(false);
  }, [leagueId, memberId]);

  useEffect(() => { refresh(); }, [refresh]);

  const pending = bids.filter((b) => b.status === 'pending');
  const resolved = bids.filter((b) => b.status === 'won' || b.status === 'lost');

  const placeBid = (teamId, dropTeamId, amount) =>
    callRpc('place_bid', {
      p_league_id: leagueId,
      p_team_id: teamId,
      p_drop_team_id: dropTeamId,
      p_amount: amount,
    }, { onSuccess: refresh });

  const cancelBid = (bidId) =>
    callRpc('cancel_bid', { p_bid_id: bidId }, { onSuccess: refresh });

  // p_bid_ids is the member's full set of pending claim ids, in the order
  // they should be tried: index 0 is rank 1. reorder_bids refuses a partial
  // list, so the caller always passes every pending id, not just the two
  // that moved.
  const reorderBids = (bidIds) =>
    callRpc('reorder_bids', { p_league_id: leagueId, p_bid_ids: bidIds }, { onSuccess: refresh });

  const getBudgets = async () => {
    const { data, error } = await supabase.rpc('get_waiver_budgets', { p_league_id: leagueId });
    if (error) return { error: error.message };
    return { budgets: data || [] };
  };

  return { bids, pending, resolved, loading, refresh, placeBid, cancelBid, reorderBids, getBudgets };
}
